/**
 * Dokumentationspaket (PV-M07).
 *
 * Ein Dokument, das der Kunde bekommt und das im Archiv bleibt:
 * Anlagenuebersicht, Belegung, Stringplan, Komponenten, Fotos, Pruefbefund.
 *
 * Alles davon liegt ohnehin schon im Projekt — hier wird es nur einmal
 * zusammengestellt und druckfertig gemacht. Nichts wird neu gerechnet und
 * nichts neu eingegeben; genau das war der Punkt im Gespraech.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { huellkasten } from '@pv/geometrie';

import { db } from '../daten/db.ts';
import { tagAnzeige } from '../datum.ts';
import { platzSchluessel, type Planung } from '../planung.ts';
import type {
  Auftragszeile,
  Flaeche,
  Foto,
  Komponente,
  Projekt,
  Projektkomponente,
  Pruefbefundzeile,
  Stringzeile,
} from '../typen.ts';
import { useFotoadresse } from './Fotos.tsx';

interface Eigenschaften {
  projekt: Projekt;
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  strings: Stringzeile[];
  projektkomponenten: Projektkomponente[];
  komponenten: Komponente[];
  auftrag: Auftragszeile | null;
  pruefbefund: Pruefbefundzeile | null;
  leistungKwp: number;
  ertragJahr: number;
  onSchliessen: () => void;
}

const KATEGORIENAMEN: Record<string, string> = {
  dach_vorher: 'Dach vorher',
  module: 'Module',
  wechselrichter: 'Wechselrichter',
  zaehlerkasten: 'Zählerkasten',
  abnahme: 'Abnahme',
  sonstiges: 'Sonstiges',
};

export function Dokumentation(e: Eigenschaften): React.JSX.Element {
  const fotos = useLiveQuery(
    () =>
      db.fotos
        .where('projekt_id')
        .equals(e.projekt.id)
        .filter((f) => !f.geloescht_am)
        .toArray(),
    [e.projekt.id],
    [] as Foto[],
  );

  const offeneStrings = e.strings.filter((s) => !s.geloescht_am);
  const offeneKomponenten = e.projektkomponenten.filter((k) => !k.geloescht_am);

  return (
    <div className="druckrahmen" onClick={e.onSchliessen}>
      <div className="druckblatt" onClick={(ereignis) => ereignis.stopPropagation()}>
        <div className="nurBildschirm knopfreihe" style={{ marginBottom: '1.5rem' }}>
          <button className="wichtig" onClick={() => window.print()}>
            Drucken oder als PDF sichern
          </button>
          <button onClick={e.onSchliessen}>Schließen</button>
        </div>

        <header style={{ borderBottom: '1px solid var(--rand)', paddingBottom: '1rem' }}>
          <p className="marke">Wania GmbH · Anlagendokumentation</p>
          <h1>{e.projekt.kunde_name}</h1>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            {[e.projekt.strasse, `${e.projekt.plz ?? ''} ${e.projekt.ort ?? ''}`.trim()]
              .filter(Boolean)
              .join(', ')}
            {e.projekt.zaehlpunktnummer && ` · Zählpunkt ${e.projekt.zaehlpunktnummer}`}
          </p>
        </header>

        <section style={{ marginTop: '1.25rem' }}>
          <h2>Anlage</h2>
          <dl className="zahlenblock">
            <div>
              <dt>Leistung</dt>
              <dd>{e.leistungKwp.toFixed(2)} kWp</dd>
            </div>
            <div>
              <dt>Prognose</dt>
              <dd>{Math.round(e.ertragJahr).toLocaleString('de-AT')} kWh/Jahr</dd>
            </div>
            <div>
              <dt>Flächen</dt>
              <dd>{e.flaechen.length}</dd>
            </div>
            <div>
              <dt>Strings</dt>
              <dd>{offeneStrings.length}</dd>
            </div>
          </dl>
        </section>

        {/* --- Belegung und Stringplan ---------------------------------- */}
        <section>
          <h2>Belegung und Stringplan</h2>
          {e.flaechen.map((flaeche) => {
            const planung = e.planungen.get(flaeche.id);
            if (!planung || planung.gesetzt.length === 0) return null;
            return (
              <figure key={flaeche.id} style={{ margin: '0 0 1.25rem' }}>
                <Flaechenzeichnung flaeche={flaeche} planung={planung} strings={offeneStrings} />
                <figcaption style={{ fontSize: '0.75rem', color: 'var(--grau)' }}>
                  {flaeche.name} · {planung.zahlen.anzahl} Module · {planung.kennzahlen.neigungGrad}
                  ° Neigung · {planung.kennzahlen.azimutGrad.toFixed(0)}° Azimut ·{' '}
                  {flaeche.dachtyp ?? 'Dachtyp nicht erfasst'}
                  {flaeche.referenzmass &&
                    ` · eingemessen auf ${flaeche.referenzmass.laengeM.toFixed(2)} m`}
                </figcaption>
              </figure>
            );
          })}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={kopf}>String</th>
                <th style={kopf}>Module</th>
                <th style={kopf}>MPP-Tracker</th>
              </tr>
            </thead>
            <tbody>
              {offeneStrings.map((string) => (
                <tr key={string.id}>
                  <td style={zelle}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '0.7rem',
                        height: '0.7rem',
                        background: string.farbe,
                        marginRight: '0.35rem',
                      }}
                    />
                    {string.name}
                  </td>
                  <td style={zelle}>{string.module.length}</td>
                  <td style={zelle}>{string.mppt ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* --- Komponenten ---------------------------------------------- */}
        <section style={{ marginTop: '1.5rem' }}>
          <h2>Komponenten</h2>
          <ul style={{ fontSize: '0.85rem' }}>
            {offeneKomponenten.map((pk) => {
              const stamm = e.komponenten.find((k) => k.id === pk.komponente_id);
              return (
                <li key={pk.id}>
                  {pk.anzahl} × {stamm ? `${stamm.hersteller} ${stamm.typ}` : 'Komponente'}
                </li>
              );
            })}
            {offeneKomponenten.length === 0 && <li>Keine Komponenten erfasst.</li>}
          </ul>
        </section>

        {/* --- Pruefbefund ---------------------------------------------- */}
        <section style={{ marginTop: '1.5rem' }}>
          <h2>Prüfbefund</h2>
          {e.pruefbefund ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={kopf}>String</th>
                    <th style={kopf}>Leerlaufspannung</th>
                    <th style={kopf}>Kurzschlussstrom</th>
                    <th style={kopf}>Isolationswiderstand</th>
                  </tr>
                </thead>
                <tbody>
                  {(e.pruefbefund.messwerte.strings ?? []).map((messwert) => (
                    <tr key={messwert.string}>
                      <td style={zelle}>{messwert.string}</td>
                      <td style={zelle}>{messwert.leerlaufspannungV ?? '—'} V</td>
                      <td style={zelle}>{messwert.kurzschlussstromA ?? '—'} A</td>
                      <td style={zelle}>{messwert.isolationswiderstandMOhm ?? '—'} MΩ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Geprüft von {e.pruefbefund.pruefer ?? '—'} am {tagAnzeige(e.pruefbefund.geprueft_am) || '—'}.
              </p>
            </>
          ) : (
            <p className="klein">Noch keine Messwerte eingetragen.</p>
          )}
        </section>

        {/* --- Fotos ---------------------------------------------------- */}
        <section style={{ marginTop: '1.5rem' }}>
          <h2>Fotos</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.5rem',
            }}
          >
            {fotos.map((foto) => (
              <Fotobild key={foto.id} fotoId={foto.id} titel={KATEGORIENAMEN[foto.kategorie] ?? foto.kategorie} />
            ))}
          </div>
          {fotos.length === 0 && <p className="klein">Noch keine Fotos aufgenommen.</p>}
        </section>

        {e.auftrag?.abgenommen_am && (
          <section style={{ marginTop: '1.5rem' }}>
            <h2>Abnahme</h2>
            <p style={{ fontSize: '0.85rem' }}>
              Abgenommen von {e.auftrag.unterschrift_name} am{' '}
              {new Date(e.auftrag.abgenommen_am).toLocaleDateString('de-AT')}.
            </p>
          </section>
        )}

        <footer style={{ marginTop: '2rem', fontSize: '0.72rem', color: 'var(--grau)' }}>
          <p>
            Kartenbild: Datenquelle basemap.at. Die Ertragsangabe ist eine Prognose. Dieses Dokument
            ersetzt keinen Fachbefund und keine Statikprüfung.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Flaechenzeichnung({
  flaeche,
  planung,
  strings,
}: {
  flaeche: Flaeche;
  planung: Planung;
  strings: Stringzeile[];
}): React.JSX.Element {
  const alle = planung.gesetzt.flatMap((platz) => platz.ecken);
  const kasten = huellkasten(alle);
  const rand = 0.5;

  const farbeJePlatz = new Map<string, string>();
  for (const string of strings) {
    for (const modul of string.module) {
      if (modul.flaecheId === flaeche.id) {
        farbeJePlatz.set(`${modul.reihe}:${modul.spalte}`, string.farbe);
      }
    }
  }

  return (
    <svg
      viewBox={`${kasten.minX - rand} ${kasten.minY - rand} ${kasten.maxX - kasten.minX + 2 * rand} ${
        kasten.maxY - kasten.minY + 2 * rand
      }`}
      style={{ width: '100%', height: 'auto', border: '1px solid var(--linie)' }}
    >
      <g transform={`translate(0, ${2 * kasten.minY + (kasten.maxY - kasten.minY)}) scale(1, -1)`}>
        {planung.gesetzt.map((platz) => {
          const schluessel = platzSchluessel(platz);
          const kante = huellkasten(platz.ecken);
          return (
            <rect
              key={schluessel}
              x={kante.minX}
              y={kante.minY}
              width={kante.maxX - kante.minX}
              height={kante.maxY - kante.minY}
              fill={farbeJePlatz.get(schluessel) ?? '#1f3a5f'}
              fillOpacity={farbeJePlatz.has(schluessel) ? 0.6 : 1}
              stroke="#000"
              strokeWidth={0.02}
            />
          );
        })}
      </g>
    </svg>
  );
}

// Kopf und Zelle kommen aus dem CSS (th, td); hier bleibt nur die Ausrichtung.
const kopf: React.CSSProperties = {};

const zelle: React.CSSProperties = {};

/** Ein Foto der Dokumentation — vom Gerät oder, mit Anmeldung, vom Server. */
function Fotobild({ fotoId, titel }: { fotoId: string; titel: string }): React.JSX.Element {
  const quelle = useFotoadresse(fotoId);
  return (
    <figure style={{ margin: 0 }}>
      <img src={quelle ?? undefined} alt={titel} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover' }} />
      <figcaption style={{ fontSize: '0.65rem', color: 'var(--grau)' }}>{titel}</figcaption>
    </figure>
  );
}
